import { Injectable } from '@angular/core';

import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';

import {
  Observable,
  throwError,
  BehaviorSubject
} from 'rxjs';

import {
  catchError,
  filter,
  finalize,
  take,
  switchMap
} from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../services/auth-service/auth.service';

@Injectable()
export class AuthInterceptor
  implements HttpInterceptor {

  private isRefreshing = false;

  private refreshTokenSubject =
    new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    // IMPORTANT:
    // Never intercept refresh API
    if (
      request.url.includes('/api/auth/refresh')
    ) {
      return next.handle(request);
    }

    const accessToken =
      this.authService.getAccessToken();

    if (accessToken) {

      request = this.addToken(
        request,
        accessToken
      );
    }

    return next.handle(request).pipe(

      catchError((error) => {

        if (
          error instanceof HttpErrorResponse &&
          error.status === 401
        ) {

          return this.handleTokenExpired(
            request,
            next
          );
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {

    return request.clone({

      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handleTokenExpired(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    if (!this.isRefreshing) {

      this.isRefreshing = true;

      this.refreshTokenSubject.next(null);

      return this.authService
        .refreshToken()
        .pipe(

          switchMap((response) => {

            const newAccessToken =
              response.accessToken;

            // setAccessToken() already ran inside authService.refreshToken();
            // no need to call it again here.

            this.refreshTokenSubject.next(
              newAccessToken
            );

            // Retry original request
            return next.handle(
              this.addToken(
                request,
                newAccessToken
              )
            );
          }),

          catchError((error) => {

            this.refreshTokenSubject.next(null);

            // Only force a logout when the refresh itself was rejected
            // as unauthorized (invalid/expired session). A transient
            // failure (network error, 500, timeout, CORS) should not
            // log the user out — just let the original request fail.
            if (
              error instanceof HttpErrorResponse &&
              (error.status === 401 || error.status === 403)
            ) {
              this.toastr.error(
                'Your session has expired. Please login again.',
                'Session Expired'
              );

              this.authService.logout();
            }

            return throwError(
              () => error
            );
          }),

          // Runs on success AND failure — guarantees isRefreshing never
          // gets stuck true (which would hang every request behind it).
          finalize(() => {
            this.isRefreshing = false;
          })
        );

    }

    // Another request is already refreshing
    return this.refreshTokenSubject.pipe(

      filter(
        (token): token is string =>
          token !== null
      ),

      take(1),

      switchMap((token) => {

        return next.handle(
          this.addToken(
            request,
            token
          )
        );
      })
    );
  }
}